import { useEffect } from "react";
import type { StoreApi } from "zustand";
import { api } from "@/lib/api-client";
import { mapApiToPayload, type ApiDocument } from "@/features/verification/lib/map-api-to-payload";
import type { VerificationState } from "@/features/verification/state/types";

/** Load one document into the pane's OWN verification store (mirrors the VS
 *  fallback fetch path — no shared prefetch cache here; each pane owns its doc). */
export function usePaneDocumentLoad(store: StoreApi<VerificationState>, documentId: string | undefined): void {
  useEffect(() => {
    if (!documentId) return;
    let cancelled = false;
    api.get<ApiDocument>(`/documents/${documentId}`).then((doc) => {
      if (cancelled) return;
      const payload = mapApiToPayload(doc);
      store.getState().loadDocument(payload);
      void store.getState().runContentValidation(documentId, "verification", { silent: true });
      payload.pages.forEach((page, idx) => {
        if (page.imageUrl) {
          fetch(page.imageUrl).then((r) => r.blob()).then((b) => createImageBitmap(b))
            .then((bitmap) => { if (!cancelled) store.getState().setPageBitmap(idx, bitmap); })
            .catch(() => {});
        }
      });
    }).catch(() => { if (!cancelled) store.setState({ isLoading: false } as Partial<VerificationState>); });
    return () => { cancelled = true; };
  }, [store, documentId]);
}
